import { css } from "@emotion/react";

import type { SizingProps } from "@phoenix/components/core/types";
import { TemplateFormats } from "@phoenix/components/templateEditor/constants";
import { usePlaygroundContext } from "@phoenix/contexts/PlaygroundContext";

export type TemplateFormatRadioGroupProps = SizingProps & {
  /**
   * Whether to show the "None" option
   * @default true
   */
  showNoneOption?: boolean;
};

const trackCSS = css`
  display: inline-flex;
  align-items: center;
  gap: 2px;
  background-color: var(--ac-global-background-color-light, #f3f4f5);
  border: 1px solid var(--ac-global-border-color-default, #e1e3e4);
  padding: 2px;
  border-radius: 9999px;
`;

const pillBaseCSS = css`
  border: none;
  border-radius: 9999px;
  padding: 4px 12px;
  font-size: 12px;
  font-weight: 500;
  cursor: pointer;
  white-space: nowrap;
  transition: all 0.15s ease;
  outline: none;

  &:focus-visible {
    box-shadow: 0 0 0 2px var(--ac-global-color-primary, #00685f);
  }
`;

const activePillCSS = css`
  ${pillBaseCSS};
  background-color: #ffffff;
  color: var(--ac-global-text-color-900, #191c1d);
  font-weight: 600;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);
`;

const inactivePillCSS = css`
  ${pillBaseCSS};
  background-color: transparent;
  color: var(--ac-global-text-color-700, #575e70);
  &:hover {
    background-color: rgba(0, 0, 0, 0.04);
    color: var(--ac-global-text-color-900, #191c1d);
  }
`;

export function TemplateFormatRadioGroup({
  showNoneOption = true,
}: TemplateFormatRadioGroupProps) {
  const templateFormat = usePlaygroundContext((state) => state.templateFormat);
  const setTemplateFormat = usePlaygroundContext(
    (state) => state.setTemplateFormat
  );

  return (
    <div css={trackCSS} role="radiogroup" aria-label="Template Format">
      <button
        type="button"
        role="radio"
        aria-checked={templateFormat === TemplateFormats.Mustache}
        css={
          templateFormat === TemplateFormats.Mustache
            ? activePillCSS
            : inactivePillCSS
        }
        onClick={() => setTemplateFormat(TemplateFormats.Mustache)}
      >
        Mustache
      </button>
      <button
        type="button"
        role="radio"
        aria-checked={templateFormat === TemplateFormats.FString}
        css={
          templateFormat === TemplateFormats.FString
            ? activePillCSS
            : inactivePillCSS
        }
        onClick={() => setTemplateFormat(TemplateFormats.FString)}
      >
        F-String
      </button>
      {showNoneOption && (
        <button
          type="button"
          role="radio"
          aria-checked={templateFormat === TemplateFormats.NONE}
          css={
            templateFormat === TemplateFormats.NONE
              ? activePillCSS
              : inactivePillCSS
          }
          onClick={() => setTemplateFormat(TemplateFormats.NONE)}
        >
          None
        </button>
      )}
    </div>
  );
}
